<template>
    <div class="player-rtp-view">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner-border text-primary" role="status"></div>
            <p>Loading RTP plan...</p>
        </div>

        <!-- No Active RTP -->
        <div v-else-if="!rtpData" class="no-rtp-state">
            <i class="bi bi-clipboard-check"></i>
            <h4>No Active RTP Protocol</h4>
            <p>Injury doesn't have an active Return-to-Play protocol yet.</p>
        </div>

        <!-- RTP Content -->
        <div v-else class="rtp-content">
            <!-- Phase Progress Bar -->
            <div v-if="!hidePhaseProgress" class="phase-progress-section">
                <h5>Recovery Progress</h5>
                <div class="phase-stepper">
                    <div v-for="phase in 5" :key="phase" class="phase-step" :class="{
                        'completed': phase < currentPhase,
                        'current': phase === currentPhase,
                        'upcoming': phase > currentPhase
                    }">
                        <div class="step-marker" :style="getPhaseMarkerStyle(phase)">
                            <i v-if="phase < currentPhase" class="bi bi-check"></i>
                            <span v-else>P{{ phase }}</span>
                        </div>
                        <div class="step-label">{{ getPhaseInfo(phase).name }}</div>
                        <div class="step-connector" v-if="phase < 5" :class="{ 'completed': phase < currentPhase }">
                        </div>
                    </div>
                </div>
            </div>

            <!-- Current Phase Card -->
            <div v-if="!hideCurrentPhaseCard" class="current-phase-card"
                :style="{ borderLeftColor: getPhaseInfo(currentPhase).color }">
                <div class="phase-header">
                    <div class="phase-badge" :style="{ backgroundColor: getPhaseInfo(currentPhase).color }">
                        Phase {{ currentPhase }}
                    </div>
                    <h3>{{ getPhaseInfo(currentPhase).name }}</h3>
                </div>
                <p class="phase-description">{{ getPhaseInfo(currentPhase).description }}</p>

                <div class="phase-details">
                    <div class="detail-item">
                        <i class="bi bi-calendar3"></i>
                        <span>{{ rtpData.days_in_phase || 0 }} days in this phase</span>
                    </div>
                    <div class="detail-item" v-if="rtpData.phase_lead_name">
                        <i class="bi bi-person"></i>
                        <span>Phase Lead: {{ rtpData.phase_lead_name }}</span>
                    </div>
                    <div class="detail-item" v-if="rtpData.next_review_date">
                        <i class="bi bi-clock"></i>
                        <span>Next Review: {{ formatDate(rtpData.next_review_date) }}</span>
                    </div>
                </div>

                <div v-if="rtpData.restrictions" class="restrictions-alert">
                    <i class="bi bi-exclamation-triangle"></i>
                    <div>
                        <strong>Current Restrictions:</strong>
                        <p>{{ rtpData.restrictions }}</p>
                    </div>
                </div>

                <!-- Phase Overview for Player -->
                <div class="phase-overview">
                    <div class="overview-header" @click="showPhaseOverview = !showPhaseOverview">
                        <h5><i class="bi bi-info-circle"></i> What to expect in Phase {{ currentPhase }}</h5>
                        <i :class="showPhaseOverview ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                    </div>
                    <div v-if="showPhaseOverview" class="overview-content">
                        <div class="overview-row">
                            <span class="label">Minimum Duration:</span>
                            <span class="value">{{ getPhaseInfo(currentPhase).minDuration }}</span>
                        </div>
                        <div class="overview-row">
                            <span class="label">Activities:</span>
                            <span class="value">{{ getPhaseInfo(currentPhase).activities }}</span>
                        </div>
                        <div class="criteria-section">
                            <span class="label">To progress to next phase, you need:</span>
                            <ul>
                                <li v-for="(criterion, idx) in getPhaseInfo(currentPhase).progressionCriteria"
                                    :key="idx">
                                    {{ criterion }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Analytics/Trends Section -->
            <div class="analytics-section" v-if="completionStats.totalCompletions > 0">
                <h5><i class="bi bi-graph-up"></i>Progress Insights</h5>

                <div class="stats-grid">
                    <div class="stat-card">
                        <div class="stat-icon compliance">
                            <i class="bi bi-check-circle"></i>
                        </div>
                        <div class="stat-info">
                            <span class="stat-value">{{ completionStats.complianceRate }}%</span>
                            <span class="stat-label">7-Day Compliance</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon streak">
                            <i class="bi bi-fire"></i>
                        </div>
                        <div class="stat-info">
                            <span class="stat-value">{{ completionStats.currentStreak }}</span>
                            <span class="stat-label">Day Streak</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon pain" :class="getPainTrendClass()">
                            <i class="bi bi-activity"></i>
                        </div>
                        <div class="stat-info">
                            <span class="stat-value">
                                {{ completionStats.avgPainLevel !== null ? completionStats.avgPainLevel.toFixed(1) : '-'
                                }}
                                <span v-if="completionStats.painTrend" class="trend-indicator"
                                    :class="completionStats.painTrend">
                                    <i
                                        :class="completionStats.painTrend === 'down' ? 'bi bi-arrow-down' : 'bi bi-arrow-up'"></i>
                                </span>
                            </span>
                            <span class="stat-label">Avg Pain Level</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon total">
                            <i class="bi bi-journal-check"></i>
                        </div>
                        <div class="stat-info">
                            <span class="stat-value">{{ completionStats.totalCompletions }}</span>
                            <span class="stat-label">Total Completions</span>
                        </div>
                    </div>
                </div>

                <!-- Pain Trend Chart -->
                <div v-if="painHistory.length > 0" class="pain-trend-section">
                    <h6>Pain Level Trend (Last 7 Days)</h6>
                    <div class="pain-chart">
                        <div class="chart-y-axis">
                            <span>10</span>
                            <span>5</span>
                            <span>0</span>
                        </div>
                        <div class="chart-bars">
                            <div v-for="(day, index) in painHistory" :key="index" class="chart-bar-container">
                                <div class="chart-bar" :style="{ height: (day.avgPain / 10 * 100) + '%' }"
                                    :class="getPainLevelClass(day.avgPain)">
                                </div>
                                <span class="bar-label">{{ day.dayLabel }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Exercises Section -->
            <div class="exercises-section">
                <div class="section-header">
                    <h5><i class="bi bi-list-check"></i>Exercises</h5>
                    <div class="view-toggle">
                        <button
                            :class="['btn btn-sm', exerciseView === 'today' ? 'btn-primary' : 'btn-outline-primary']"
                            @click="exerciseView = 'today'">
                            Today
                        </button>
                        <button :class="['btn btn-sm', exerciseView === 'all' ? 'btn-primary' : 'btn-outline-primary']"
                            @click="exerciseView = 'all'">
                            All Phases
                        </button>
                    </div>
                </div>

                <!-- Today's View -->
                <div v-if="exerciseView === 'today'" class="today-exercises">
                    <div v-if="todayExercises.length === 0" class="no-exercises">
                        <i class="bi bi-calendar-check"></i>
                        <p>No exercises assigned for today</p>
                    </div>

                    <div v-else class="exercise-checklist">
                        <div class="today-progress">
                            <span>{{ completedToday }}/{{ todayExercises.length }} completed today</span>
                            <div class="progress">
                                <div class="progress-bar" :style="{ width: todayProgressPercent + '%' }"></div>
                            </div>
                        </div>

                        <div v-for="exercise in todayExercises" :key="exercise.assignment_id" class="exercise-item"
                            :class="{
                                'completed': exercise.completed_today,
                                'from-previous-phase': getExercisePhase(exercise) < currentPhase
                            }">
                            <div class="exercise-checkbox" @click="toggleExercise(exercise)">
                                <i v-if="exercise.completed_today" class="bi bi-check-circle-fill"></i>
                                <i v-else class="bi bi-circle"></i>
                            </div>
                            <div class="exercise-info" @click="showExerciseDetails(exercise)">
                                <div class="exercise-name-row">
                                    <span class="exercise-name">{{ exercise.exercise_name }}</span>
                                    <span v-if="getExercisePhase(exercise) < currentPhase"
                                        class="carried-forward-badge">
                                        P{{ getExercisePhase(exercise) }}
                                    </span>
                                </div>
                                <span class="exercise-meta">
                                    {{ exercise.custom_sets_reps || exercise.sets_reps || '' }}
                                    <span v-if="exercise.duration"> | {{ exercise.duration }}</span>
                                </span>
                            </div>
                            <div class="exercise-actions">
                                <button v-if="!exercise.completed_today" class="btn btn-sm btn-success"
                                    @click="openCompletionModal(exercise)">
                                    Complete
                                </button>
                                <div v-else class="completion-info">
                                    <span v-if="exercise.pain_level !== null" class="pain-badge"
                                        :class="getPainLevelClass(exercise.pain_level)">
                                        Pain: {{ exercise.pain_level }}
                                    </span>
                                    <button class="btn btn-sm btn-outline-secondary" @click="undoCompletion(exercise)">
                                        <i class="bi bi-arrow-counterclockwise"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- All Phases View -->
                <div v-else class="all-phases-exercises">
                    <div v-for="phase in 5" :key="phase" class="phase-exercises-group"
                        :class="{ 'current-phase': phase === currentPhase, 'future-phase': phase > currentPhase }">
                        <div class="phase-group-header" :style="{ borderLeftColor: getPhaseInfo(phase).color }">
                            <span class="phase-badge-sm" :style="{ backgroundColor: getPhaseInfo(phase).color }">
                                P{{ phase }}
                            </span>
                            <span class="phase-name">{{ getPhaseInfo(phase).name }}</span>
                            <span v-if="phase === currentPhase" class="current-badge">Current</span>
                            <span v-if="phase > currentPhase" class="locked-badge">
                                <i class="bi bi-lock"></i> Upcoming
                            </span>
                            <span class="exercise-count">{{ getPhaseExerciseCount(phase) }} exercises</span>
                        </div>

                        <div v-if="exercisesByPhase[phase]?.length > 0" class="phase-exercise-list">
                            <div v-for="exercise in exercisesByPhase[phase]"
                                :key="exercise.assignment_id || exercise.exercise_id" class="exercise-row"
                                :class="{ 'disabled': phase > currentPhase }">
                                <div class="exercise-icon">
                                    <i :class="getCategoryIcon(exercise.category)"></i>
                                </div>
                                <div class="exercise-details">
                                    <span class="exercise-name">{{ exercise.exercise_name }}</span>
                                    <span class="exercise-specs">
                                        {{ exercise.custom_sets_reps || exercise.sets_reps }}
                                        <span v-if="exercise.intensity"> | {{ exercise.intensity }}</span>
                                    </span>
                                </div>
                                <div class="exercise-status">
                                    <span v-if="phase < currentPhase" class="status-completed">
                                        <i class="bi bi-check-circle"></i> Phase Complete
                                    </span>
                                    <span v-else-if="phase === currentPhase && exercise.completed_today"
                                        class="status-done-today">
                                        <i class="bi bi-check"></i> Done Today
                                    </span>
                                    <button v-else-if="phase === currentPhase" class="btn btn-sm btn-outline-success"
                                        @click="openCompletionModal(exercise)">
                                        Complete
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-else class="no-phase-exercises">
                            <p>No exercises assigned for this phase</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Recent Activity Timeline -->
            <div class="timeline-section">
                <h5><i class="bi bi-clock-history"></i> Recent Activity</h5>
                <div class="mini-timeline">
                    <div v-for="(event, index) in recentActivity" :key="index" class="timeline-event"
                        :class="event.type">
                        <div class="event-marker">
                            <i :class="getEventIcon(event.type)"></i>
                        </div>
                        <div class="event-content">
                            <span class="event-title">{{ event.title }}</span>
                            <span class="event-date">{{ event.signOffBy }} - {{ formatDateTime(event.date) }}</span>
                            <p v-if="event.details" class="event-details">{{ event.details }}</p>
                        </div>
                    </div>
                    <div v-if="recentActivity.length === 0" class="no-activity">
                        <p>No recent activity</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Exercise Completion Modal -->
        <div v-if="showCompletionModal" class="completion-modal-overlay" @click.self="closeCompletionModal">
            <div class="completion-modal">
                <div class="modal-header">
                    <h4>Complete Exercise</h4>
                    <button class="close-btn" @click="closeCompletionModal">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="exercise-summary">
                        <h5>{{ selectedExercise?.exercise_name }}</h5>
                        <p>{{ selectedExercise?.custom_sets_reps || selectedExercise?.sets_reps }}</p>
                    </div>

                    <!-- Quick Complete Option -->
                    <div class="quick-complete">
                        <button class="btn btn-success btn-lg" @click="quickComplete">
                            <i class="bi bi-check-circle"></i> Quick Complete
                        </button>
                        <p class="text-muted">Just mark as done</p>
                    </div>

                    <div class="divider">
                        <span>or add details</span>
                    </div>

                    <!-- Detailed Logging -->
                    <div class="detailed-logging">
                        <div class="form-group">
                            <label>Pain Level During Exercise</label>
                            <div class="pain-scale">
                                <button v-for="n in 11" :key="n - 1"
                                    :class="['pain-btn', { selected: completionForm.painLevel === n - 1 }]"
                                    :style="{ backgroundColor: getPainColor(n - 1) }"
                                    @click="completionForm.painLevel = n - 1">
                                    {{ n - 1 }}
                                </button>
                            </div>
                            <div class="pain-labels">
                                <span>No Pain</span>
                                <span>Moderate</span>
                                <span>Severe</span>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Difficulty</label>
                            <div class="difficulty-options">
                                <button v-for="option in difficultyOptions" :key="option.value"
                                    :class="['difficulty-btn', { selected: completionForm.difficulty === option.value }]"
                                    @click="completionForm.difficulty = option.value">
                                    <i :class="option.icon"></i>
                                    {{ option.label }}
                                </button>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Notes (optional)</label>
                            <textarea v-model="completionForm.notes" class="form-control"
                                placeholder="Any observations, discomfort, or feedback..." rows="3"></textarea>
                        </div>

                        <button class="btn btn-primary btn-block" @click="submitCompletion" :disabled="submitting">
                            <span v-if="submitting">
                                <span class="spinner-border spinner-border-sm"></span> Saving...
                            </span>
                            <span v-else>
                                <i class="bi bi-check-lg"></i> Complete with Details
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Exercise Details Modal -->
        <div v-if="showExerciseDetailsModal && exerciseDetails" class="exercise-details-modal-overlay"
            @click.self="closeExerciseDetails">
            <div class="exercise-details-modal">
                <div class="modal-header">
                    <h4>{{ exerciseDetails.exercise_name }}</h4>
                    <button class="close-btn" @click="closeExerciseDetails">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="detail-row">
                        <label>Category:</label>
                        <span>{{ exerciseDetails.category }}</span>
                    </div>
                    <div class="detail-row">
                        <label>Sets/Reps:</label>
                        <span>{{ exerciseDetails.custom_sets_reps || exerciseDetails.sets_reps }}</span>
                    </div>
                    <div v-if="exerciseDetails.duration" class="detail-row">
                        <label>Duration:</label>
                        <span>{{ exerciseDetails.duration }}</span>
                    </div>
                    <div v-if="exerciseDetails.intensity" class="detail-row">
                        <label>Intensity:</label>
                        <span>{{ exerciseDetails.intensity }}</span>
                    </div>
                    <div v-if="exerciseDetails.description" class="detail-row full-width">
                        <label>Description:</label>
                        <p>{{ exerciseDetails.description }}</p>
                    </div>
                    <div v-if="exerciseDetails.precautions" class="detail-row full-width precautions">
                        <label><i class="bi bi-exclamation-triangle"></i> Precautions:</label>
                        <p>{{ exerciseDetails.precautions }}</p>
                    </div>
                    <div v-if="exerciseDetails.video_url" class="detail-row full-width">
                        <label>Video Guide:</label>
                        <a :href="exerciseDetails.video_url" target="_blank" class="btn btn-outline-primary btn-sm">
                            <i class="bi bi-play-circle"></i> Watch Video
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ReturnToPlayService, RTP_PHASES } from "@/services/rtp-service.js";

export default {
    name: "PlayerRTPView",
    props: {
        injuryId: {
            type: [Number, String],
            required: true
        },
        playerId: {
            type: [Number, String],
            required: true
        },
        hidePhaseProgress: {
            type: Boolean,
            default: false
        },
        hideCurrentPhaseCard: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: true,
            rtpData: null,
            exercises: [],
            completionHistory: [],
            exerciseView: 'today',
            showPhaseOverview: true,
            showCompletionModal: false,
            showExerciseDetailsModal: false,
            selectedExercise: null,
            exerciseDetails: null,
            submitting: false,
            completionForm: {
                painLevel: null,
                difficulty: null,
                notes: ''
            },
            difficultyOptions: [
                { value: 1, label: 'Easy', icon: 'bi bi-emoji-smile' },
                { value: 2, label: 'Moderate', icon: 'bi bi-emoji-neutral' },
                { value: 3, label: 'Hard', icon: 'bi bi-emoji-frown' },
                { value: 4, label: 'Very Hard', icon: 'bi bi-emoji-dizzy' }
            ],
            completionStats: {
                complianceRate: 0,
                currentStreak: 0,
                avgPainLevel: null,
                painTrend: null,
                totalCompletions: 0
            },
            painHistory: [],
            recentActivity: []
        };
    },
    computed: {
        currentPhase() {
            // Backend returns rtp_phase from the injury table
            return parseInt(this.rtpData?.rtp_phase) || 1;
        },
        todayExercises() {
            // Show exercises for current phase AND uncompleted exercises from previous phases
            // This way exercises "follow" the player until completed
            return this.exercises.filter(e => {
                const exercisePhase = parseInt(e.phase_number) || parseInt(e.assigned_phase);
                const isCurrentPhase = exercisePhase === this.currentPhase;
                const isPreviousPhaseUncompleted = exercisePhase < this.currentPhase && !e.completed_today;
                return isCurrentPhase || isPreviousPhaseUncompleted;
            });
        },
        completedToday() {
            return this.todayExercises.filter(e => e.completed_today).length;
        },
        todayProgressPercent() {
            if (this.todayExercises.length === 0) return 0;
            return Math.round((this.completedToday / this.todayExercises.length) * 100);
        },
        exercisesByPhase() {
            const grouped = { 1: [], 2: [], 3: [], 4: [], 5: [] };
            this.exercises.forEach(ex => {
                const phase = parseInt(ex.phase_number) || parseInt(ex.assigned_phase);
                if (grouped[phase]) {
                    grouped[phase].push(ex);
                }
            });
            return grouped;
        }
    },
    async mounted() {
        await this.loadData();
    },
    watch: {
        injuryId: {
            handler() {
                this.loadData();
            }
        }
    },
    methods: {
        async loadData() {
            this.loading = true;
            try {
                // Load RTP data
                console.log('PlayerRTPView: Loading RTP data for injury:', this.injuryId);
                const rtpResponse = await ReturnToPlayService.getRTPByInjuryId(this.injuryId);
                console.log('PlayerRTPView: RTP Response:', rtpResponse.data);

                if (rtpResponse.data?.success && rtpResponse.data.injury) {
                    // Map the injury data to rtpData format
                    const injury = rtpResponse.data.injury;
                    this.rtpData = {
                        ...injury,
                        phase_history: rtpResponse.data.phase_history || [],
                        days_in_phase: rtpResponse.data.days_in_current_phase,
                        current_maturity: rtpResponse.data.current_maturity,
                        maturity_outdated: rtpResponse.data.maturity_outdated
                    };
                    console.log('PlayerRTPView: RTP Data set:', this.rtpData);
                } else {
                    console.warn('PlayerRTPView: No RTP data found or API error', rtpResponse.data);
                }

                // Load assigned exercises
                console.log('PlayerRTPView: Loading exercises for injury:', this.injuryId, 'player:', this.playerId);
                const exercisesResponse = await ReturnToPlayService.getPlayerAssignedExercises({
                    injury_id: this.injuryId,
                    player_id: this.playerId,
                    include_completed: true
                });
                console.log('PlayerRTPView: Exercises Response:', exercisesResponse.data);
                if (exercisesResponse.data?.success) {
                    this.exercises = exercisesResponse.data.exercises || [];
                    console.log('PlayerRTPView: Loaded', this.exercises.length, 'exercises');
                    console.log('PlayerRTPView: Current phase:', this.currentPhase, 'type:', typeof this.currentPhase);
                    if (this.exercises.length > 0) {
                        console.log('PlayerRTPView: First exercise phase:', this.exercises[0].phase_number, 'type:', typeof this.exercises[0].phase_number);
                    }
                }

                // Load completion stats
                await this.loadCompletionStats();

                // Load recent activity
                await this.loadRecentActivity();

            } catch (error) {
                console.error('Error loading RTP data:', error);
            } finally {
                this.loading = false;
            }
        },

        async loadCompletionStats() {
            try {
                const response = await ReturnToPlayService.getPlayerComplianceStats({
                    player_id: this.playerId,
                    injury_id: this.injuryId,
                    days: 7
                });
                if (response.data?.success) {
                    this.completionStats = {
                        complianceRate: response.data.compliance_rate || 0,
                        currentStreak: response.data.current_streak || 0,
                        avgPainLevel: response.data.avg_pain_level,
                        painTrend: response.data.pain_trend,
                        totalCompletions: response.data.total_completions || 0
                    };
                    this.painHistory = response.data.daily_pain || [];
                }
            } catch (error) {
                console.error('Error loading compliance stats:', error);
            }
        },

        async loadRecentActivity() {
            try {
                const response = await ReturnToPlayService.getExerciseCompletionHistory({
                    player_id: this.playerId,
                    injury_id: this.injuryId
                });
                if (response.data?.success) {
                    // Combine phase history and exercise completions
                    const events = [];

                    // Add phase history events
                    if (this.rtpData?.phase_history) {
                        this.rtpData.phase_history.forEach(ph => {
                            events.push({
                                type: 'phase-change',
                                title: `Advanced to Phase ${ph.phase_number}: ${ph.phase_name}`,
                                date: ph.entered_date || ph.created_at,
                                signOffBy: ph.cleared_by_name,
                                details: ph.sign_off_comments
                            });
                        });
                    }

                    // Add recent completions (last 10)
                    const completions = response.data.completions || [];
                    completions.slice(0, 10).forEach(c => {
                        events.push({
                            type: 'exercise-complete',
                            title: `Completed: ${c.exercise_name}`,
                            date: c.completed_at,
                            details: c.pain_level !== null ? `Pain: ${c.pain_level}/10` : null
                        });
                    });

                    // Sort by date desc and take top 10
                    events.sort((a, b) => new Date(b.date) - new Date(a.date));
                    this.recentActivity = events.slice(0, 10);
                }
            } catch (error) {
                console.error('Error loading activity:', error);
            }
        },

        getPhaseInfo(phase) {
            return RTP_PHASES[phase] || { name: 'Unknown', color: '#6c757d', bgColor: '#f8f9fa', description: '' };
        },

        getPhaseMarkerStyle(phase) {
            const info = this.getPhaseInfo(phase);
            if (phase < this.currentPhase) {
                return { backgroundColor: info.color, color: 'white' };
            } else if (phase === this.currentPhase) {
                return { backgroundColor: info.color, color: 'white', boxShadow: `0 0 0 4px ${info.bgColor}` };
            }
            return { backgroundColor: '#e9ecef', color: '#6c757d' };
        },

        formatDate(dateStr) {
            if (!dateStr) return '-';
            return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        },

        formatDateTime(dateStr) {
            if (!dateStr) return '-';
            return new Date(dateStr).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
            });
        },

        getCategoryIcon(category) {
            const icons = {
                'Mobility': 'bi bi-arrows-expand',
                'Strength': 'bi bi-lightning',
                'Cardio': 'bi bi-heart-pulse',
                'Balance': 'bi bi-bullseye',
                'Flexibility': 'bi bi-person-arms-up',
                'Sport-Specific': 'bi bi-dribbble'
            };
            return icons[category] || 'bi bi-activity';
        },

        getPhaseExerciseCount(phase) {
            return this.exercisesByPhase[phase]?.length || 0;
        },

        getPainColor(level) {
            if (level <= 2) return '#28a745';
            if (level <= 4) return '#7cb342';
            if (level <= 6) return '#ffc107';
            if (level <= 8) return '#ff9800';
            return '#dc3545';
        },

        getPainLevelClass(level) {
            if (level === null || level === undefined) return '';
            if (level <= 2) return 'pain-low';
            if (level <= 5) return 'pain-medium';
            return 'pain-high';
        },

        getPainTrendClass() {
            if (this.completionStats.painTrend === 'down') return 'improving';
            if (this.completionStats.painTrend === 'up') return 'worsening';
            return '';
        },

        getEventIcon(type) {
            const icons = {
                'phase-change': 'bi bi-arrow-up-circle',
                'exercise-complete': 'bi bi-check-circle',
                'setback': 'bi bi-arrow-down-circle'
            };
            return icons[type] || 'bi bi-circle';
        },

        toggleExercise(exercise) {
            if (exercise.completed_today) {
                this.undoCompletion(exercise);
            } else {
                this.openCompletionModal(exercise);
            }
        },

        openCompletionModal(exercise) {
            this.selectedExercise = exercise;
            this.completionForm = {
                painLevel: null,
                difficulty: null,
                notes: ''
            };
            this.showCompletionModal = true;
        },

        closeCompletionModal() {
            this.showCompletionModal = false;
            this.selectedExercise = null;
        },

        async quickComplete() {
            await this.submitCompletion(true);
        },

        async submitCompletion(quickMode = false) {
            if (!this.selectedExercise) return;

            this.submitting = true;
            try {
                const response = await ReturnToPlayService.completeExercise({
                    assignment_id: this.selectedExercise.assignment_id,
                    player_id: this.playerId,
                    injury_id: this.injuryId,
                    completed_by: this.playerId,
                    pain_level: quickMode ? null : this.completionForm.painLevel,
                    difficulty_rating: quickMode ? null : this.completionForm.difficulty,
                    notes: quickMode ? null : this.completionForm.notes
                });

                if (response.data?.success) {
                    // Update local state
                    const idx = this.exercises.findIndex(e => e.assignment_id === this.selectedExercise.assignment_id);
                    if (idx !== -1) {
                        this.exercises[idx].completed_today = true;
                        this.exercises[idx].completion_id = response.data.completion_id;
                        this.exercises[idx].pain_level = this.completionForm.painLevel;
                    }

                    this.closeCompletionModal();
                    this.$toast?.success('Exercise completed!');

                    // Reload stats
                    await this.loadCompletionStats();
                    await this.loadRecentActivity();
                } else {
                    this.$toast?.error(response.data?.message || 'Failed to complete exercise');
                }
            } catch (error) {
                console.error('Error completing exercise:', error);
                this.$toast?.error('Error completing exercise');
            } finally {
                this.submitting = false;
            }
        },

        async undoCompletion(exercise) {
            if (!exercise.completion_id) return;

            try {
                const response = await ReturnToPlayService.undoExerciseCompletion(exercise.completion_id);
                if (response.data?.success) {
                    const idx = this.exercises.findIndex(e => e.assignment_id === exercise.assignment_id);
                    if (idx !== -1) {
                        this.exercises[idx].completed_today = false;
                        this.exercises[idx].completion_id = null;
                        this.exercises[idx].pain_level = null;
                    }
                    this.$toast?.success('Completion undone');
                    await this.loadCompletionStats();
                }
            } catch (error) {
                console.error('Error undoing completion:', error);
            }
        },

        showExerciseDetails(exercise) {
            this.exerciseDetails = exercise;
            this.showExerciseDetailsModal = true;
        },

        closeExerciseDetails() {
            this.showExerciseDetailsModal = false;
            this.exerciseDetails = null;
        },

        getExercisePhase(exercise) {
            return parseInt(exercise.phase_number) || parseInt(exercise.assigned_phase) || 1;
        }
    }
};
</script>

<style scoped lang="scss">
.player-rtp-view {
    padding: 20px;
}

.loading-state,
.no-rtp-state {
    text-align: center;
    padding: 60px 20px;
    color: #6c757d;

    i {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
    }

    h4 {
        margin-bottom: 8px;
    }
}

/* Phase Progress */
.phase-progress-section {
    margin-bottom: 24px;

    h5 {
        margin-bottom: 16px;
        font-size: 16px;
        font-weight: 600;
    }
}

.phase-stepper {
    display: flex;
    justify-content: space-between;
    position: relative;
    padding: 0 20px;
}

.phase-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 1;

    .step-marker {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        z-index: 1;
        transition: all 0.3s;

        i {
            font-size: 20px;
        }
    }

    .step-label {
        margin-top: 8px;
        font-size: 11px;
        color: #6c757d;
        text-align: center;
        max-width: 80px;
    }

    .step-connector {
        position: absolute;
        top: 24px;
        left: 50%;
        width: 100%;
        height: 3px;
        background: #e9ecef;
        z-index: 0;

        &.completed {
            background: #28a745;
        }
    }

    &.current .step-marker {
        transform: scale(1.1);
    }
}

/* Current Phase Card */
.current-phase-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    border-left: 4px solid;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .phase-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;

        .phase-badge {
            padding: 6px 14px;
            border-radius: 20px;
            color: white;
            font-weight: 600;
            font-size: 13px;
        }

        h3 {
            margin: 0;
            font-size: 20px;
        }
    }

    .phase-description {
        color: #6c757d;
        margin-bottom: 16px;
        font-size: 14px;
    }

    .phase-details {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;

        .detail-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;

            i {
                color: #6c757d;
            }
        }
    }

    .restrictions-alert {
        display: flex;
        gap: 12px;
        margin-top: 16px;
        padding: 12px;
        background: #fff3cd;
        border-radius: 8px;
        border: 1px solid #ffeeba;

        i {
            color: #856404;
            font-size: 20px;
        }

        strong {
            display: block;
            color: #856404;
            margin-bottom: 4px;
        }

        p {
            margin: 0;
            color: #856404;
            font-size: 14px;
        }
    }

    /* Phase Overview */
    .phase-overview {
        margin-top: 16px;
        background: #f8f9fa;
        border-radius: 8px;
        border: 1px solid #e9ecef;
        overflow: hidden;
    }

    .overview-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background: #e9ecef;
        }

        h5 {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            color: #495057;

            i {
                color: #6c757d;
            }
        }
    }

    .overview-content {
        padding: 16px;
        border-top: 1px solid #e9ecef;
        background: white;
    }

    .overview-row {
        display: flex;
        gap: 8px;
        margin-bottom: 12px;

        .label {
            font-size: 13px;
            color: #6c757d;
            font-weight: 500;
            min-width: 120px;
        }

        .value {
            font-size: 13px;
            color: #212529;
        }
    }

    .criteria-section {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #e9ecef;

        .label {
            font-size: 13px;
            color: #6c757d;
            font-weight: 500;
            display: block;
            margin-bottom: 8px;
        }

        ul {
            margin: 0;
            padding-left: 20px;

            li {
                font-size: 13px;
                color: #495057;
                padding: 4px 0;
                position: relative;

                &::marker {
                    color: #28a745;
                }
            }
        }
    }
}

/* Analytics Section */
.analytics-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h5 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
        font-size: 16px;

        i {
            color: #007bff;
        }
    }
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
}

.stat-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 10px;

    .stat-icon {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;

        &.compliance {
            background: #e3f2fd;
            color: #1976d2;
        }

        &.streak {
            background: #fff3e0;
            color: #f57c00;
        }

        &.pain {
            background: #e8f5e9;
            color: #388e3c;

            &.improving {
                background: #e8f5e9;
                color: #388e3c;
            }

            &.worsening {
                background: #ffebee;
                color: #c62828;
            }
        }

        &.total {
            background: #f3e5f5;
            color: #7b1fa2;
        }
    }

    .stat-info {
        display: flex;
        flex-direction: column;

        .stat-value {
            font-size: 22px;
            font-weight: 700;

            .trend-indicator {
                font-size: 14px;
                margin-left: 4px;

                &.down {
                    color: #388e3c;
                }

                &.up {
                    color: #c62828;
                }
            }
        }

        .stat-label {
            font-size: 12px;
            color: #6c757d;
        }
    }
}

/* Pain Trend Chart */
.pain-trend-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;

    h6 {
        margin-bottom: 12px;
        font-size: 13px;
        color: #6c757d;
    }
}

.pain-chart {
    display: flex;
    height: 120px;

    .chart-y-axis {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding-right: 8px;
        font-size: 10px;
        color: #6c757d;
    }

    .chart-bars {
        display: flex;
        flex: 1;
        align-items: flex-end;
        gap: 8px;
        padding-bottom: 20px;
    }

    .chart-bar-container {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
        justify-content: flex-end;

        .chart-bar {
            width: 100%;
            max-width: 30px;
            border-radius: 4px 4px 0 0;
            transition: height 0.3s;

            &.pain-low {
                background: #28a745;
            }

            &.pain-medium {
                background: #ffc107;
            }

            &.pain-high {
                background: #dc3545;
            }
        }

        .bar-label {
            margin-top: 4px;
            font-size: 10px;
            color: #6c757d;
        }
    }
}

/* Exercises Section */
.exercises-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h5 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
            font-size: 16px;

            i {
                color: #007bff;
            }
        }

        .view-toggle {
            display: flex;
            gap: 4px;
        }
    }
}

/* Today's Exercises */
.today-progress {
    margin-bottom: 16px;

    span {
        font-size: 14px;
        color: #6c757d;
    }

    .progress {
        height: 8px;
        margin-top: 8px;
        background: #e9ecef;
        border-radius: 4px;
        overflow: hidden;

        .progress-bar {
            background: #28a745;
            transition: width 0.3s;
        }
    }
}

.exercise-checklist {
    .exercise-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px;
        background: #f8f9fa;
        border-radius: 10px;
        margin-bottom: 10px;
        transition: all 0.2s;

        &.completed {
            background: #e8f5e9;

            .exercise-name {
                text-decoration: line-through;
                color: #6c757d;
            }
        }

        &.from-previous-phase {
            border-left: 3px solid #fd7e14;
            background: #fff8f0;

            &.completed {
                background: #e8f5e9;
                border-left-color: #28a745;
            }
        }

        .exercise-checkbox {
            cursor: pointer;
            font-size: 24px;

            .bi-check-circle-fill {
                color: #28a745;
            }

            .bi-circle {
                color: #ced4da;
            }
        }

        .exercise-info {
            flex: 1;
            cursor: pointer;

            .exercise-name-row {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .exercise-name {
                display: block;
                font-weight: 500;
                font-size: 15px;
            }

            .carried-forward-badge {
                font-size: 10px;
                padding: 2px 6px;
                background: #fd7e14;
                color: white;
                border-radius: 10px;
                font-weight: 600;
            }

            .exercise-meta {
                font-size: 13px;
                color: #6c757d;
            }
        }

        .exercise-actions {
            display: flex;
            align-items: center;
            gap: 8px;

            .completion-info {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .pain-badge {
                font-size: 12px;
                padding: 4px 8px;
                border-radius: 12px;

                &.pain-low {
                    background: #e8f5e9;
                    color: #388e3c;
                }

                &.pain-medium {
                    background: #fff3cd;
                    color: #856404;
                }

                &.pain-high {
                    background: #ffebee;
                    color: #c62828;
                }
            }
        }
    }
}

.no-exercises {
    text-align: center;
    padding: 40px;
    color: #6c757d;

    i {
        font-size: 32px;
        margin-bottom: 8px;
        opacity: 0.5;
    }
}

/* All Phases View */
.all-phases-exercises {
    .phase-exercises-group {
        margin-bottom: 16px;
        border: 1px solid #e9ecef;
        border-radius: 10px;
        overflow: hidden;

        &.future-phase {
            opacity: 0.6;
        }

        &.current-phase {
            border-color: #007bff;
            box-shadow: 0 0 0 1px #007bff;
        }
    }

    .phase-group-header {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        background: #f8f9fa;
        border-left: 3px solid;

        .phase-badge-sm {
            padding: 3px 10px;
            border-radius: 12px;
            color: white;
            font-size: 12px;
            font-weight: 600;
        }

        .phase-name {
            font-weight: 500;
            flex: 1;
        }

        .current-badge {
            font-size: 11px;
            padding: 2px 8px;
            background: #007bff;
            color: white;
            border-radius: 10px;
        }

        .locked-badge {
            font-size: 11px;
            color: #6c757d;

            i {
                margin-right: 4px;
            }
        }

        .exercise-count {
            font-size: 12px;
            color: #6c757d;
        }
    }

    .phase-exercise-list {
        padding: 8px;
    }

    .exercise-row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: 8px;
        transition: background 0.2s;

        &:hover:not(.disabled) {
            background: #f8f9fa;
        }

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .exercise-icon {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            background: #e9ecef;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6c757d;
        }

        .exercise-details {
            flex: 1;

            .exercise-name {
                display: block;
                font-weight: 500;
                font-size: 14px;
            }

            .exercise-specs {
                font-size: 12px;
                color: #6c757d;
            }
        }

        .exercise-status {

            .status-completed,
            .status-done-today {
                font-size: 12px;
                color: #28a745;

                i {
                    margin-right: 4px;
                }
            }
        }
    }

    .no-phase-exercises {
        padding: 16px;
        text-align: center;
        color: #6c757d;
        font-size: 13px;
    }
}

/* Timeline Section */
.timeline-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h5 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
        font-size: 16px;

        i {
            color: #007bff;
        }
    }
}

.mini-timeline {
    .timeline-event {
        display: flex;
        gap: 12px;
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
            border-bottom: none;
        }

        .event-marker {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }

        &.phase-change .event-marker {
            background: #e3f2fd;
            color: #1976d2;
        }

        &.exercise-complete .event-marker {
            background: #e8f5e9;
            color: #388e3c;
        }

        &.setback .event-marker {
            background: #ffebee;
            color: #c62828;
        }

        .event-content {
            flex: 1;

            .event-title {
                display: block;
                font-weight: 500;
                font-size: 14px;
            }

            .event-date {
                font-size: 12px;
                color: #6c757d;
            }

            .event-details {
                font-size: 13px;
                color: #6c757d;
                margin-top: 4px;
            }
        }
    }

    .no-activity {
        text-align: center;
        padding: 20px;
        color: #6c757d;
    }
}

/* Completion Modal */
.completion-modal-overlay,
.exercise-details-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1060;
    padding: 20px;
}

.completion-modal,
.exercise-details-modal {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    overflow-y: auto;

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #e9ecef;

        h4 {
            margin: 0;
            font-size: 18px;
        }

        .close-btn {
            background: none;
            border: none;
            font-size: 28px;
            cursor: pointer;
            color: #6c757d;
        }
    }

    .modal-body {
        padding: 20px;
    }
}

.exercise-summary {
    text-align: center;
    margin-bottom: 20px;

    h5 {
        margin-bottom: 4px;
    }

    p {
        color: #6c757d;
        margin: 0;
    }
}

.quick-complete {
    text-align: center;
    margin-bottom: 20px;

    .btn-lg {
        padding: 12px 32px;
        font-size: 16px;
    }

    p {
        margin-top: 8px;
        font-size: 13px;
    }
}

.divider {
    text-align: center;
    margin: 20px 0;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 50%;
        height: 1px;
        background: #e9ecef;
    }

    span {
        background: white;
        padding: 0 16px;
        color: #6c757d;
        font-size: 13px;
        position: relative;
    }
}

.detailed-logging {
    .form-group {
        margin-bottom: 20px;

        label {
            display: block;
            font-weight: 500;
            margin-bottom: 8px;
            font-size: 14px;
        }
    }

    .pain-scale {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;

        .pain-btn {
            border: 2px solid transparent;
            border-radius: 8px;
            color: white;
            font-weight: 600;
            cursor: pointer;
            transition: transform 0.2s;
            padding: 0.6rem;

            &.selected {
                transform: scale(1.15);
                border-color: #333;
            }
        }
    }

    .pain-labels {
        display: flex;
        justify-content: space-between;
        margin-top: 8px;
        font-size: 11px;
        color: #6c757d;
    }

    .difficulty-options {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .difficulty-btn {
            flex: 1;
            min-width: 80px;
            padding: 12px 8px;
            border: 2px solid #e9ecef;
            border-radius: 10px;
            background: white;
            cursor: pointer;
            text-align: center;
            transition: all 0.2s;

            i {
                display: block;
                font-size: 24px;
                margin-bottom: 4px;
            }

            &.selected {
                border-color: #007bff;
                background: #e3f2fd;
            }
        }
    }

    .btn-block {
        width: 100%;
        padding: 12px;
    }
}

/* Exercise Details Modal */
.exercise-details-modal {
    max-width: 500px;

    .detail-row {
        display: flex;
        margin-bottom: 12px;

        label {
            font-weight: 500;
            width: 100px;
            flex-shrink: 0;
            color: #6c757d;
        }

        span,
        p {
            flex: 1;
        }

        p {
            margin: 0;
        }

        &.full-width {
            flex-direction: column;

            label {
                width: auto;
                margin-bottom: 4px;
            }
        }

        &.precautions {
            background: #fff3cd;
            padding: 12px;
            border-radius: 8px;
            margin-top: 8px;

            label {
                color: #856404;
            }

            p {
                color: #856404;
            }
        }
    }
}

/* Responsive - Tablet */
@media (max-width: 768px) {
    .player-rtp-view {
        padding: 0;
    }

    .phase-progress-section {
        margin-bottom: 16px;

        h5 {
            font-size: 14px;
            margin-bottom: 12px;
        }
    }

    .phase-stepper {
        padding: 0 8px;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .phase-step {
        min-width: 60px;

        .step-marker {
            width: 40px;
            height: 40px;
            font-size: 12px;
        }

        .step-label {
            font-size: 10px;
            margin-top: 6px;
        }

        .step-connector {
            top: 20px;
        }
    }

    .current-phase-card {
        padding: 16px;
        margin-bottom: 16px;

        .phase-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;

            h3 {
                font-size: 18px;
            }
        }

        .phase-description {
            font-size: 13px;
        }

        .phase-details {
            gap: 8px;

            .detail-item {
                font-size: 13px;
            }
        }
    }

    .analytics-section {
        padding: 16px;
        margin-bottom: 16px;

        h5 {
            font-size: 14px;
        }
    }

    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }

    .stat-card {
        padding: 12px;
        flex-direction: column;
        text-align: center;
        gap: 8px;

        .stat-icon {
            width: 40px;
            height: 40px;

            i {
                font-size: 18px;
            }
        }

        .stat-info {
            .stat-value {
                font-size: 20px;
            }

            .stat-label {
                font-size: 11px;
            }
        }
    }

    .pain-trend-section {
        h6 {
            font-size: 13px;
        }

        .pain-chart {
            height: 120px;

            .chart-y-axis span {
                font-size: 10px;
            }

            .bar-label {
                font-size: 9px;
            }
        }
    }

    .exercises-section {
        padding: 16px;
        margin-bottom: 16px;

        .section-header {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;

            h5 {
                font-size: 14px;
            }

            .view-toggle {
                display: flex;
                width: 100%;

                button {
                    flex: 1;
                }
            }
        }
    }

    .today-progress {
        font-size: 13px;
        padding: 10px;
    }

    .exercise-item {
        padding: 12px;
        flex-wrap: wrap;

        .exercise-checkbox {
            flex-shrink: 0;

            i {
                font-size: 22px;
            }
        }

        .exercise-info {
            flex: 1;
            min-width: 0;

            .exercise-name {
                font-size: 14px;
            }

            .exercise-meta {
                font-size: 12px;
            }
        }

        .exercise-actions {
            width: 100%;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #e9ecef;
            justify-content: space-between;

            button {
                flex: 1;
            }

            .completion-info {
                display: flex;
                width: 100%;
                justify-content: space-between;
                align-items: center;
            }
        }
    }

    .phase-exercises-group {
        margin-bottom: 12px;

        .phase-group-header {
            padding: 10px 12px;
            flex-wrap: wrap;
            gap: 6px;

            .phase-name {
                font-size: 13px;
            }

            .exercise-count {
                width: 100%;
                margin-top: 4px;
            }
        }
    }

    .exercise-row {
        padding: 12px;
        flex-wrap: wrap;

        .exercise-icon {
            width: 32px;
            height: 32px;

            i {
                font-size: 14px;
            }
        }

        .exercise-details {
            flex: 1;
            min-width: calc(100% - 50px);

            .exercise-name {
                font-size: 14px;
            }

            .exercise-specs {
                font-size: 12px;
            }
        }

        .exercise-status {
            width: 100%;
            margin-top: 8px;
            text-align: right;
        }
    }

    .timeline-section {
        padding: 16px;
        margin-bottom: 16px;

        h5 {
            font-size: 14px;
        }
    }

    .timeline-event {
        padding: 10px 0 10px 36px;

        .event-marker {
            width: 28px;
            height: 28px;
            left: 0;

            i {
                font-size: 12px;
            }
        }

        .event-content {
            .event-title {
                font-size: 13px;
            }

            .event-date {
                font-size: 11px;
            }

            .event-details {
                font-size: 12px;
            }
        }
    }

    /* Completion Modal Mobile */
    .completion-modal {
        width: 95%;
        max-width: none;
        margin: 10px;
        max-height: 90vh;

        .modal-header {
            padding: 14px 16px;

            h4 {
                font-size: 16px;
            }
        }

        .modal-body {
            padding: 16px;
        }

        .exercise-summary {
            padding: 12px;

            h5 {
                font-size: 15px;
            }
        }

        .quick-complete button {
            width: 100%;
            padding: 14px;
        }

        .pain-scale {
            gap: 4px;

            .pain-btn {
                width: 30px;
                height: 30px;
                font-size: 12px;
            }
        }

        .modal-actions {
            flex-direction: column;
            gap: 10px;

            button {
                width: 100%;
            }
        }
    }
}

/* Responsive - Mobile Small */
@media (max-width: 480px) {
    .phase-stepper {
        padding: 0 4px;
    }

    .phase-step {
        min-width: 50px;

        .step-marker {
            width: 32px;
            height: 32px;
            font-size: 10px;
        }

        .step-label {
            font-size: 8px;
            max-width: 50px;
            text-align: center;
            line-height: 1.2;
        }

        .step-connector {
            top: 16px;
        }
    }

    .stats-grid {
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }

    .stat-card {
        padding: 10px 8px;

        .stat-icon {
            width: 36px;
            height: 36px;

            i {
                font-size: 16px;
            }
        }

        .stat-info {
            .stat-value {
                font-size: 18px;
            }

            .stat-label {
                font-size: 10px;
            }
        }
    }

    .current-phase-card {
        padding: 12px;

        .phase-badge {
            font-size: 11px;
            padding: 4px 10px;
        }

        .phase-header h3 {
            font-size: 16px;
        }

        .restrictions-alert {
            padding: 10px;
            font-size: 12px;
        }

        .phase-overview {
            .overview-header h5 {
                font-size: 13px;
            }

            .overview-content {
                font-size: 12px;
            }
        }
    }

    .exercise-item {
        padding: 10px;

        .exercise-checkbox i {
            font-size: 20px;
        }

        .exercise-name-row {
            flex-wrap: wrap;
            gap: 4px;
        }

        .carried-forward-badge {
            font-size: 9px;
        }
    }

    .pain-trend-section {
        .pain-chart {
            height: 100px;
        }

        .chart-bar-container {
            min-width: 28px;
        }
    }

    .completion-modal {
        .pain-scale {
            flex-wrap: wrap;
            justify-content: center;

            .pain-btn {
                width: 28px;
                height: 28px;
                font-size: 11px;
            }
        }
    }
}
</style>
